import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { useTelegram } from './TelegramProvider'
import { createTmaAuthService, type TmaAuthMode, type TmaAuthSession } from '../services/telegramAuth'

type AuthStatus = 'loading' | 'authenticated' | 'unauthorized' | 'error'

interface AuthState {
  status: AuthStatus
  mode: TmaAuthMode
  session: TmaAuthSession | null
  reason?: string
}

const authService = createTmaAuthService()

const initialState: AuthState = {
  status: 'loading',
  mode: authService.mode,
  session: null,
}

const AuthContext = createContext<AuthState>(initialState)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const telegram = useTelegram()
  const [state, setState] = useState<AuthState>(initialState)

  useEffect(() => {
    if (!telegram.isReady) {
      setState((current) => ({
        ...current,
        status: 'loading',
      }))
      return
    }

    let isCancelled = false

    setState((current) => ({
      ...current,
      status: 'loading',
      reason: undefined,
    }))

    authService
      .authenticate({
        initData: telegram.initData,
        isTelegram: telegram.isTelegram,
        telegramId: telegram.telegramUser?.id,
        fallbackTelegramId: telegram.fallbackTelegramId,
        fallbackRole: telegram.fallbackRole,
      })
      .then((result) => {
        if (isCancelled) {
          return
        }

        if (result.status === 'authenticated') {
          setState({
            status: 'authenticated',
            mode: authService.mode,
            session: result.session,
          })
          return
        }

        if (result.status === 'error') {
          setState({
            status: 'error',
            mode: authService.mode,
            session: null,
            reason: result.reason,
          })
          return
        }

        setState({
          status: 'unauthorized',
          mode: authService.mode,
          session: null,
          reason: result.reason,
        })
      })
      .catch((error) => {
        if (isCancelled) {
          return
        }

        setState({
          status: 'error',
          mode: authService.mode,
          session: null,
          reason: error instanceof Error ? error.message : String(error),
        })
      })

    return () => {
      isCancelled = true
    }
  }, [
    telegram.fallbackRole,
    telegram.fallbackTelegramId,
    telegram.initData,
    telegram.isReady,
    telegram.isTelegram,
    telegram.telegramUser?.id,
  ])

  const value = useMemo(() => state, [state])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = (): AuthState => useContext(AuthContext)
